import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/api';
import { jwtExpiresAtMs, shouldRefreshAccessToken } from '@/utils/sessionRefresh';

export const SESSION_EXPIRED_NOTICE_KEY = 'session_expired_notice_pending';

export interface User {
  id: number;
  email: string;
  phone: string | null;
  first_name: string;
  last_name: string;
  middle_name?: string;
  user_type: 'individual' | 'legal_entity' | 'agent' | 'supplier' | 'aggregator';
  status: 'pending' | 'active' | 'suspended' | 'blocked';
  role: 'user';
  is_verified: boolean; // верификация email
  verification_status: string; // верификация личности: "pending" | "approved" | "rejected" | "in_review"
  verification_completed_at?: string;
  phone_confirmed?: boolean;
  citizenship?: string | null;
  residency_category?: 'resident' | 'non_resident_in_kg' | 'non_resident_abroad' | null;
  preferred_currency: string;
  language: string;
  created_at: string;
  date_of_birth?: string; // YYYY-MM-DD для расчёта возраста (P2P 18-25)
  passport_number?: string | null;
  tax_id?: string | null;
  use_test_services?: boolean;
}

export const useUserStore = defineStore('user', () => {
  const user = ref<User | null>(null);
  const token = ref<string | null>((() => { try { return localStorage.getItem('token') } catch { return null } })());
  const refreshTokenValue = ref<string | null>((() => { try { return localStorage.getItem('refresh_token') } catch { return null } })());
  let lastActivityAt = Date.now();
  let refreshInFlight: Promise<boolean | 'offline'> | null = null;

  const noteActivity = () => { lastActivityAt = Date.now(); };

  const storeTokens = (accessToken: string, refreshToken?: string | null) => {
    token.value = accessToken;
    localStorage.setItem('token', accessToken);
    if (refreshToken) {
      refreshTokenValue.value = refreshToken;
      localStorage.setItem('refresh_token', refreshToken);
    }
  };

  const bearerForRefresh = () => {
    if (refreshTokenValue.value) return refreshTokenValue.value;
    const expiresAt = jwtExpiresAtMs(token.value);
    if (token.value && expiresAt !== null && expiresAt > Date.now()) return token.value;
    return null;
  };
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Вычисляемые свойства
  const isAuthenticated = computed(() => !!token.value);
  const isActive = computed(() => user.value?.status === 'active');
  const fullName = computed(() => {
    if (!user.value) return '';
    return [user.value.first_name, user.value.last_name].filter(Boolean).join(' ');
  });
  const initials = computed(() => {
    const first = user.value?.first_name?.[0] || '';
    const last = user.value?.last_name?.[0] || '';
    return `${first}${last}`.toUpperCase();
  });
  const role = computed(() => 'user'); // В клиентском фронтенде всегда 'user'
  
  // Проверка верификации личности (KYC) - НЕ путать с верификацией email
  const isIdentityVerified = computed(() => user.value?.verification_status === 'approved');
  const isPhoneConfirmed = computed(() => user.value?.phone_confirmed !== false);
  const isAgent = computed(() => user.value?.user_type === 'agent');

  // Возраст пользователя 18-25 лет (для P2P-ограничений НБКР)
  const isUserAge18to25 = computed(() => {
    const dob = user.value?.date_of_birth;
    if (!dob) return false;
    const birthDate = new Date(typeof dob === 'string' ? dob : (dob as any));
    if (isNaN(birthDate.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age >= 18 && age <= 25;
  });

  const markSessionExpired = () => {
    try {
      localStorage.setItem(SESSION_EXPIRED_NOTICE_KEY, '1');
    } catch {
      // В private mode хранилище может быть недоступно.
    }
  };

  // Методы
  const login = async (credentials: { username: string; password: string }) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const response = await api.login(credentials);
      
      // Проверяем, требуется ли 2FA
      if (response.data.require_2fa) {
        return { require2FA: true, message: response.data.message };
      }
      
      const { access_token, refresh_token } = response.data;
      storeTokens(access_token, refresh_token);
      
      // Получаем информацию о пользователе
      await fetchUser();
      
      return { success: true };
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Ошибка входа';
      return { success: false, error: error.value };
    } finally {
      isLoading.value = false;
    }
  };

  const loginWith2FA = async (credentials: { email: string; password: string; code: string }) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const response = await api.loginWith2FA(credentials);
      
      const { access_token, refresh_token } = response.data;
      storeTokens(access_token, refresh_token);
      
      // Получаем информацию о пользователе
      await fetchUser();
      
      return true;
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Ошибка входа с 2FA';
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const register = async (userData: {
    email: string;
    phone: string;
    password: string;
    first_name: string;
    last_name: string;
    middle_name?: string;
    user_type: 'individual' | 'legal_entity';
  }) => {
    isLoading.value = true;
    error.value = null;
    
    try {
      await api.register(userData);
      return true;
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Ошибка регистрации';
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchUser = async () => {
    if (!token.value) return;
    
    const expiresAt = jwtExpiresAtMs(token.value);
    if (expiresAt === null) {
      logout();
      return;
    }
    if (expiresAt <= Date.now()) {
      const refreshed = await refreshToken();
      if (refreshed !== true) return;
    }
    
    try {
      console.log('Fetching user data...');
      console.log('Token:', token.value);
      
      const response = await api.getCurrentUser();
      console.log('User data received:', response.data);
      user.value = response.data;
      
      // Язык будет установлен в App.vue при инициализации
      // useI18n нельзя вызывать в store, только в компонентах
    } catch (err: any) {
      console.error('Ошибка получения данных пользователя:', err);
      
      // Если это сетевая ошибка (бэкенд недоступен), не очищаем токен
      if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
        console.log('Backend unavailable, keeping token for later retry');
        return;
      }
      
      // Уведомление планируем только для завершившейся/недействительной сессии.
      if (err.response?.status === 401) {
        console.log('Token expired or invalid, logging out...');
        logout(true);
      } else if (err.response?.status === 403) {
        logout();
      }
    }
  };

  const logout = (sessionExpired = false) => {
    if (sessionExpired) {
      markSessionExpired();
    }
    user.value = null;
    token.value = null;
    refreshTokenValue.value = null;
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('consent_public_offer_012');
    error.value = null;
  };

  const refreshToken = async (): Promise<boolean | 'offline'> => {
    if (refreshInFlight) return refreshInFlight;

    const task = (async (): Promise<boolean | 'offline'> => {
      const bearer = bearerForRefresh();
      if (!bearer) {
        if (token.value || refreshTokenValue.value) logout(true);
        return false;
      }
      try {
        const response = await api.refreshToken(bearer);
        const { access_token, refresh_token } = response.data;
        storeTokens(access_token, refresh_token);
        return true;
      } catch (err: any) {
        const offline = !err?.response && err?.code !== 'ERR_CANCELED';
        if (offline) return 'offline';
        logout(true);
        return false;
      }
    })();

    refreshInFlight = task;
    try {
      return await task;
    } finally {
      if (refreshInFlight === task) refreshInFlight = null;
    }
  };

  const maybeRefresh = () => {
    if (!shouldRefreshAccessToken({
      now: Date.now(),
      accessExpiresAtMs: jwtExpiresAtMs(token.value),
      hasRefreshToken: !!refreshTokenValue.value,
      tabVisible: typeof document === 'undefined' || document.visibilityState === 'visible',
      lastActivityAt,
    })) return;
    void refreshToken();
  };

  if (typeof window !== 'undefined') {
    window.setInterval(maybeRefresh, 60_000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    });
    window.addEventListener('pointerdown', noteActivity, { passive: true });
    window.addEventListener('keydown', noteActivity);
  }

  // Проверка прав доступа
  const hasRole = (roles: string | string[]) => {
    if (!user.value) return false;
    
    const userRole = user.value.role;
    if (typeof roles === 'string') {
      return userRole === roles;
    }
    return roles.includes(userRole);
  };

  const canAccess = (requiredRole: string) => {
    return hasRole(requiredRole);
  };

  const updateLanguage = async (language: string) => {
    if (!user.value) return false;
    
    try {
      const response = await api.updateUserLanguage(language);
      user.value = response.data;
      return true;
    } catch (err: any) {
      error.value = err.response?.data?.detail || 'Ошибка обновления языка';
      return false;
    }
  };

  // Инициализация при загрузке
  const init = async () => {
    if (token.value) {
      console.log('Initializing user store with token...');
      
      const expiresAt = jwtExpiresAtMs(token.value);
      if (expiresAt === null || expiresAt <= Date.now()) {
        const refreshed = await refreshToken();
        if (refreshed !== true) return;
      }
      
      try {
        await fetchUser();
        console.log('User data restored successfully');
      } catch (err: any) {
        console.error('Failed to restore user data:', err);
        
        // Если это сетевая ошибка (бэкенд недоступен), не очищаем токен
        if (err.code === 'ECONNREFUSED' || err.message?.includes('Network Error')) {
          console.log('Backend unavailable during init, keeping token for later retry');
          return;
        }
        
        // Если не удалось получить данные пользователя по другим причинам, очищаем токен
        logout();
      }
    } else if (refreshTokenValue.value) {
      const refreshed = await refreshToken();
      if (refreshed === true) await fetchUser();
    } else {
      console.log('No token found, user not authenticated');
    }
    maybeRefresh();
  };

  return {
    // Состояние
    user,
    token,
    isLoading,
    error,
    
    // Вычисляемые свойства
    isAuthenticated,
    isActive,
    fullName,
    initials,
    role,
    isIdentityVerified,
    isPhoneConfirmed,
    isAgent,
    
    // Методы
    login,
    loginWith2FA,
    register,
    fetchUser,
    logout,
    refreshToken,
    hasRole,
    canAccess,
    updateLanguage,
    init
  };
}); 